import Icon, { AppIconProps } from "@Components/generic/icon";
import useI18n from "@i18n";
import { Button, ButtonProps, Dialog, DialogProps, Stack, StackProps, TextField, TextFieldProps, Typography, TypographyProps, useTheme } from "@mui/material";
import { ChangeEvent, HTMLInputTypeAttribute, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface IConfirmationDialog {
  open: boolean;
  title?: string;
  message?: string;
  iconProps?: AppIconProps;
  confirmLabel?: string;
  cancelLabel?: string;
  disableCancel?: boolean;
  disableConfirm?: boolean;
  inputField?: boolean;
  inputLabel?: string;
  inputType?: HTMLInputTypeAttribute;
  inputFieldProps?: TextFieldProps;
  buttonProps?: ButtonProps[];
  dialogProps?: DialogProps;
  titleProps?: TypographyProps;
  messageProps?: TypographyProps;
  stackProps?: StackProps;
  buttonStackProps?: StackProps;
  onClose?: () => void;
  onConfirm?: (input?: string) => void;
  onCancel?: () => void;
  blockNavigation?: boolean;
  children?: ReactNode;
}

const ConfirmationDialog = ({
  open,
  title,
  message,
  iconProps,
  confirmLabel = "confirm",
  cancelLabel = "cancel",
  disableCancel = false,
  disableConfirm = false,
  inputField = false,
  inputLabel = "",
  inputType = "text",
  inputFieldProps,
  buttonProps,
  dialogProps,
  titleProps,
  messageProps,
  stackProps,
  buttonStackProps,
  onClose = () => {},
  onConfirm = () => {},
  onCancel = () => {},
  blockNavigation = true,
  children,
}: IConfirmationDialog) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { i18n } = useI18n();
  const [inputValue, setInputValue] = useState("");

  const confirmButtonProps = buttonProps?.[0];
  const cancelButtonProps = buttonProps?.[1] ?? confirmButtonProps;

  useEffect(() => {
    if (!blockNavigation || !open) {
      return;
    }

    const interceptNavigation = (event: PopStateEvent) => {
      event.preventDefault();
      navigate(1);
    };

    window.addEventListener("popstate", interceptNavigation);

    return () => {
      window.removeEventListener("popstate", interceptNavigation);
    };
  }, [blockNavigation, navigate, open]);

  return (
    <Dialog
      {...dialogProps}
      open={open}
      onClose={onClose}
      PaperProps={{
        ...dialogProps?.PaperProps,
        sx: {
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          backgroundColor: theme.palette.dsSurface.v100,
          padding: theme => `${theme.spacing(4)} ${theme.spacing(4)}`,
          ...dialogProps?.PaperProps?.sx,
        },
      }}
    >
      <Stack spacing={2} {...stackProps}>
        {iconProps && (
          <Icon
            sx={{
              marginTop: dialogProps?.fullScreen ? theme.spacing(-8) : 0,
              marginBottom: theme.spacing(2),
            }}
            {...iconProps}
          />
        )}

        {title && (
          <Typography variant="h5" {...titleProps}>
            {title}
          </Typography>
        )}

        {message && (
          <Typography variant="body1" {...messageProps}>
            {message}
          </Typography>
        )}

        {children}

        {inputField && (
          <form autoComplete="off">
            <TextField
              autoFocus
              margin="dense"
              label={inputLabel}
              type={inputType}
              autoComplete="off"
              fullWidth
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setInputValue(event.target.value);
              }}
              {...inputFieldProps}
            />
          </form>
        )}

        <Stack spacing={2} direction="row" paddingTop={theme.spacing(2)} justifyContent="center" {...buttonStackProps}>
          {!disableConfirm && (
            <Button color="primary" autoFocus onClick={() => onConfirm(inputValue)} {...confirmButtonProps}>
              {i18n(confirmLabel)}
            </Button>
          )}

          {!disableCancel && (
            <Button color="primary" onClick={onCancel} {...cancelButtonProps}>
              {i18n(cancelLabel)}
            </Button>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default ConfirmationDialog;